import React, { useEffect, useMemo, useState } from "react";
import { Capacitor } from '@capacitor/core';
import { FaWhatsapp } from 'react-icons/fa';
import useConversationHistory from '../offline/useConversationHistory';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { domainForAgent, inferConversationAgentId } from '../chatDomains';

const ChatHistory = ({ onSelectConversation, currentConversationId, refreshTrigger, onLogout, projectId, scope, agentId }) => {
  // `projectId`: filter to that project's chats. `scope`: "general" returns
  // only chats not in any project. Default (both undefined) returns everything.
  const { conversations, isLoading, error } = useConversationHistory({ refreshTrigger, projectId, scope });
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform() && error?.response?.status === 401) {
      localStorage.removeItem('token');
      if (onLogout) onLogout();
      else navigate('/login');
    }
  }, [error, navigate, onLogout]);

  const filteredConversations = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return [...conversations]
      .filter((conversation) => !agentId || domainForAgent(inferConversationAgentId(conversation)) === domainForAgent(agentId))
      .filter((conversation) => !query || (conversation.title || '').toLocaleLowerCase().includes(query))
      .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));
  }, [conversations, search, agentId]);
  const visibleConversations = showAll || search.trim() ? filteredConversations : filteredConversations.slice(0, 6);
  const handleNewChat = () => onSelectConversation(null, agentId || 'default');

  return (
    <div className="chat-history">
      <div className="chat-history-header">
        <h3>{t(showAll ? 'workspace:allConversations' : 'workspace:recentConversations')}</h3>
        {isLoading && conversations.length > 0 && (
          <span className="chat-history-loading-inline">
            {t('history.loading', 'Chargement...')}
          </span>
        )}
        <button onClick={handleNewChat} className="new-chat-btn" title={t('workspace:newChat')}>
          {t('workspace:newChat')}
        </button>
      </div>

      <label className="ws-history-search"><span className="ws-sr-only">{t('workspace:searchConversations')}</span>
        <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t('workspace:searchConversations')} />
      </label>
      <div className="chat-history-body">
        {isLoading && conversations.length === 0 ? (
          <p className="loading-msg">{t('history.loading', 'Chargement...')}</p>
        ) : filteredConversations.length === 0 ? (
          <p className="no-conversation-msg">
            {t(search.trim() ? 'workspace:noSearchResults' : 'workspace:noHistory')}
          </p>
        ) : (
          <ul className="conversation-list">
            {visibleConversations.map((conv) => (
              <li
                key={conv.id}
                className={conv.id === currentConversationId ? 'active' : ''}
                title={conv.title || 'Conversation'}
              >
                <button type="button" className="ws-history-entry" onClick={() => onSelectConversation(conv, inferConversationAgentId(conv))} aria-current={conv.id === currentConversationId ? 'page' : undefined}>
                <div className="conversation-title">
                  {conv.title || "Conversation sans titre"}
                </div>
                <div className="conversation-date">
                  <span className="ws-history-domain">{t(`workspace:domain_${domainForAgent(inferConversationAgentId(conv))}`)}</span>
                  {conv.source === 'whatsapp' && (
                    <span className="ws-history-whatsapp" title="WhatsApp"><FaWhatsapp aria-hidden="true" /> WhatsApp</span>
                  )}

                  {(conv.createdAt || conv.updatedAt) && new Date(conv.createdAt || conv.updatedAt).toLocaleDateString(i18n.language, {
                    day: 'numeric',
                    month: 'short'
                  })}
                  {/* In a shared project the list mixes everyone's chats, so
                      name whoever wrote the ones that are not yours (#72).
                      `mine` is computed server-side from the JWT. */}
                  {conv.mine === false && conv.author && (
                    <span className="conversation-author">
                      {' · '}
                      {conv.author.name || conv.author.email}
                    </span>
                  )}
                </div>
                </button>
              </li>
            ))}
          </ul>
        )}
        {!search.trim() && filteredConversations.length > 6 && <button type="button" className="ws-text-button ws-history-more" onClick={() => setShowAll((value) => !value)}>
          {t(showAll ? 'workspace:recentConversations' : 'workspace:allConversations')}
        </button>}
      </div>
    </div>
  );
};

export default ChatHistory;
